import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, Clock, Copy } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { publicBookingInput, type PublicBookingOptions, type PublicBookingResult } from '@barbeiropro/contracts';
import { MaskedInput } from '@/components/shared/masked-input';
import { HttpError } from '@/lib/api';
import { fmtTime, money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { formatInTimeZone } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';
import { useDocumentMeta, bookingOptionsKey } from './public-site-root';
import { pub } from './pub-api';
import { displayStyle } from './theme';

type Service = PublicBookingOptions['services'][number];
type Barber = PublicBookingOptions['barbers'][number];
interface Slot {
  startsAt: string;
  endsAt: string;
  barberId: string;
}

const customerSchema = publicBookingInput.shape.customer;
const STEPS = ['Serviço', 'Profissional', 'Horário', 'Seus dados'] as const;

const longDate = (iso: string, tz: string) => formatInTimeZone(iso, tz, "EEEE, d 'de' MMMM 'às' HH:mm", { locale: ptBR });

function addDays(ymd: string, n: number) {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const field = 'w-full border-2 bg-transparent px-3.5 py-3 text-base focus-visible:outline-2 focus-visible:outline-offset-2';
const line = { borderColor: 'var(--s-line)' };

function Choice({ selected, onClick, children, className }: { selected?: boolean; onClick: () => void; children: React.ReactNode; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn('w-full border-2 p-4 text-left transition-colors hover:border-[var(--s-ink)]', className)}
      style={{ borderColor: selected ? 'var(--s-ink)' : 'var(--s-line)', background: selected ? 'color-mix(in srgb, var(--s-accent) 16%, var(--s-bg))' : undefined }}
    >
      {children}
    </button>
  );
}

export function BookingPage({ slug, orgName }: { slug: string; orgName: string }) {
  useDocumentMeta(`Agendar horário · ${orgName}`, `Escolha serviço, profissional e horário na ${orgName}.`);
  const qc = useQueryClient();
  const opts = useQuery({ queryKey: bookingOptionsKey(slug), queryFn: () => pub<PublicBookingOptions>('GET', `/public/${slug}/booking-options`), retry: false });

  const [step, setStep] = useState(0);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [service, setService] = useState<Service | null>(null);
  const [barber, setBarber] = useState<Barber | 'any' | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [result, setResult] = useState<PublicBookingResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const idemKey = useRef<string>('');
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, [step, result]);

  const data = opts.data;
  const tz = data?.organization.timezone ?? 'America/Sao_Paulo';
  const branch = data ? (data.branches.find((b) => b.id === branchId) ?? data.branches[0]) : undefined;

  const barbersFor = useMemo(
    () => (data && service && branch ? data.barbers.filter((b) => b.branchId === branch.id && b.serviceIds.includes(service.id)) : []),
    [data, service, branch],
  );

  const days = useMemo(() => {
    if (!data) return [];
    const today = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
    return Array.from({ length: Math.min(data.policy.maxAdvanceDays, 90) + 1 }, (_, i) => addDays(today, i));
  }, [data, tz]);

  const availKey = ['site-public', slug, 'availability', service?.id, barber === 'any' ? 'any' : barber?.id, date, branch?.id] as const;
  const avail = useQuery({
    queryKey: availKey,
    enabled: step === 2 && Boolean(service && date && branch && barber),
    retry: false,
    queryFn: () =>
      pub<Slot[]>('GET', `/public/${slug}/availability`, undefined, {
        serviceId: service!.id,
        barberId: barber && barber !== 'any' ? barber.id : undefined,
        date: date!,
        branchId: branch!.id,
      }),
  });

  const slots = useMemo(() => {
    const seen = new Map<string, Slot>();
    for (const s of avail.data ?? []) if (!seen.has(s.startsAt)) seen.set(s.startsAt, s);
    return [...seen.values()].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }, [avail.data]);

  const form = useForm<z.input<typeof customerSchema> & { website: string }, unknown, z.output<typeof customerSchema> & { website: string }>({
    resolver: zodResolver(customerSchema.extend({ website: z.string().max(0).optional() }) as never),
    defaultValues: { name: '', phone: '', email: '', marketingConsent: false, website: '' },
  });
  const { errors, isSubmitting } = form.formState;

  if (opts.isPending) return <div role="status" className="px-6 py-40 text-center" style={{ color: 'var(--s-muted)' }}>Carregando opções…</div>;
  if (opts.isError || !data) {
    const nf = opts.error instanceof HttpError && opts.error.status === 404;
    return (
      <Wrap>
        <h1 style={displayStyle} className="text-5xl">{nf ? 'Agendamento indisponível' : 'Não foi possível carregar'}</h1>
        <p className="mt-4">{nf ? 'Esta barbearia não recebe agendamentos por aqui.' : 'Verifique a conexão e tente de novo.'}</p>
        {!nf && <button className="mt-4 font-semibold underline" onClick={() => opts.refetch()}>Tentar de novo</button>}
      </Wrap>
    );
  }
  if (!data.policy.allowPublicBooking)
    return (
      <Wrap>
        <h1 style={displayStyle} className="text-5xl">Agendamento pelo site desligado</h1>
        <p className="mt-4 max-w-md">No momento a {data.organization.name} não recebe agendamentos online. Fale com a barbearia pelos contatos no rodapé.</p>
      </Wrap>
    );

  function pickService(s: Service) {
    setService(s);
    setBarber(null);
    setSlot(null);
    setStep(1);
  }

  function pickBarber(b: Barber | 'any') {
    setBarber(b);
    setSlot(null);
    setStep(2);
  }

  function pickDate(d: string) {
    setDate(d);
    setSlot(null);
    setNotice(null);
  }

  function pickSlot(s: Slot) {
    setSlot(s);
    idemKey.current = crypto.randomUUID();
    setStep(3);
  }

  const slotBarber = slot ? data.barbers.find((b) => b.id === slot.barberId) : undefined;

  async function submit(values: z.output<typeof customerSchema> & { website: string }) {
    if (!service || !slot || !branch) return;
    try {
      const r = await pub<PublicBookingResult>('POST', `/public/${slug}/bookings`, {
        serviceId: service.id,
        barberId: slot.barberId,
        branchId: branch.id,
        startsAt: slot.startsAt,
        customer: { name: values.name, phone: values.phone, email: values.email, marketingConsent: values.marketingConsent },
        idempotencyKey: idemKey.current,
        website: values.website ?? '',
      });
      setResult(r);
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setSlot(null);
        setNotice('Esse horário acabou de ser ocupado. Escolha outro, já atualizamos a lista.');
        await qc.invalidateQueries({ queryKey: ['site-public', slug, 'availability'] });
        setStep(2);
      } else if (e instanceof HttpError && e.status === 422) {
        const fe = e.fieldErrors();
        if (Object.keys(fe).some((k) => k.startsWith('customer'))) toast.error('Confira seus dados: ' + Object.values(fe).join(' '));
        else toast.error(e.message);
      } else if (e instanceof HttpError && e.status === 404) toast.error('Não encontramos esse serviço ou profissional. Recomece o agendamento.');
      else if (e instanceof HttpError && e.status === 429) toast.error('Muitas tentativas. Aguarde um minuto e tente de novo.');
      else toast.error('Não foi possível concluir o agendamento. Tente de novo.');
    }
  }

  // ----------------------------------------------------------- confirmação
  if (result) {
    const manage = `${window.location.origin}/b/${slug}/agendamento/${result.manageToken}`;
    const pending = result.status === 'requested';
    return (
      <Wrap>
        <div className="flex items-center gap-3">
          <span className="flex size-12 items-center justify-center" style={{ background: 'var(--s-accent)', color: 'var(--s-on-accent)' }}><Check aria-hidden /></span>
          <h1 ref={heading} tabIndex={-1} className="text-5xl outline-none" style={displayStyle}>{pending ? 'Pedido enviado' : 'Horário marcado'}</h1>
        </div>
        <dl className="mt-8 grid max-w-lg gap-3 border-y-2 py-6" style={{ borderColor: 'var(--s-ink)' }}>
          <Row k="Serviço" v={result.serviceName} />
          <Row k="Profissional" v={result.barberName} />
          <Row k="Quando" v={longDate(result.startsAt, tz)} />
          {branch && <Row k="Onde" v={`${branch.name} · ${branch.address.street}, ${branch.address.number}`} />}
        </dl>
        {pending && <p className="mt-5 max-w-lg">A barbearia precisa confirmar este horário. Você verá a mudança de situação pelo link abaixo.</p>}
        <p className="mt-5 max-w-lg" role="status">
          {result.emailStatus === 'queued' && 'Vamos enviar os detalhes para o e-mail informado.'}
          {result.emailStatus === 'skipped_unconfigured' && 'O envio de e-mails não está disponível no momento, então você não receberá confirmação por e-mail. Guarde o link abaixo: é por ele que você consulta ou cancela o agendamento.'}
          {result.emailStatus === 'not_requested' && 'Guarde o link abaixo para consultar ou cancelar o agendamento.'}
        </p>
        <div className="mt-4 flex max-w-lg items-center gap-2">
          <input readOnly value={manage} aria-label="Link para gerenciar o agendamento" className={cn(field, 'text-sm')} style={line} onFocus={(e) => e.currentTarget.select()} />
          <button
            type="button"
            className="inline-flex size-12 shrink-0 items-center justify-center border-2"
            style={line}
            aria-label="Copiar link"
            onClick={() => navigator.clipboard?.writeText(manage).then(() => toast.success('Link copiado'))}
          >
            <Copy className="size-4" aria-hidden />
          </button>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/b/${slug}/agendamento/${result.manageToken}`} className="inline-flex min-h-12 items-center px-7 font-semibold" style={{ background: 'var(--s-ink)', color: 'var(--s-bg)' }}>
            Ver meu agendamento
          </Link>
          <Link to={`/b/${slug}`} className="inline-flex min-h-12 items-center border-2 px-7 font-semibold" style={{ borderColor: 'var(--s-ink)' }}>
            Voltar ao site
          </Link>
        </div>
      </Wrap>
    );
  }

  const back = step > 0 && (
    <button type="button" onClick={() => setStep(step - 1)} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold underline underline-offset-4">
      <ArrowLeft className="size-4" aria-hidden /> Voltar
    </button>
  );
  const titles = ['Qual serviço você quer?', 'Com quem?', 'Quando?', 'Seus dados'];

  return (
    <Wrap>
      <nav aria-label="Etapas do agendamento" className="mb-10">
        <ol className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold">
          {STEPS.map((s, i) => (
            <li key={s} aria-current={i === step ? 'step' : undefined} className={cn(i > step && 'opacity-45')}>
              <button type="button" disabled={i >= step} onClick={() => setStep(i)} className={cn('inline-flex items-center gap-2', i < step && 'underline underline-offset-4')}>
                <span className="tabular flex size-6 items-center justify-center text-xs" style={{ background: i === step ? 'var(--s-ink)' : 'transparent', color: i === step ? 'var(--s-bg)' : 'inherit', border: '2px solid var(--s-ink)' }}>
                  {i < step ? <Check className="size-3.5" aria-hidden /> : i + 1}
                </span>
                {s}
              </button>
            </li>
          ))}
        </ol>
      </nav>
      {back}
      <h1 ref={heading} tabIndex={-1} className="mb-8 text-[clamp(2.5rem,9cqw,4.5rem)] outline-none" style={displayStyle}>{titles[step]}</h1>

      {notice && (
        <p role="alert" className="mb-6 flex items-start gap-3 border-2 p-4" style={{ borderColor: 'var(--s-accent)' }}>
          <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden /> {notice}
        </p>
      )}

      {step === 0 && (
        <div className="grid gap-4">
          {data.branches.length > 1 && (
            <div className="grid max-w-sm gap-1.5">
              <label htmlFor="bk-branch" className="text-sm font-semibold">Unidade</label>
              <select id="bk-branch" className={field} style={line} value={branch?.id} onChange={(e) => setBranchId(e.target.value)}>
                {data.branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          {data.services.length === 0 && <p>Nenhum serviço disponível para agendamento online.</p>}
          <ul className="grid gap-3">
            {data.services.map((s) => (
              <li key={s.id}>
                <Choice selected={service?.id === s.id} onClick={() => pickService(s)}>
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-lg font-semibold">{s.name}</span>
                    <span className="tabular font-bold">{money(s.priceCents)}</span>
                  </div>
                  <p className="mt-1 flex items-center gap-1.5 text-sm" style={{ color: 'var(--s-muted)' }}>
                    <Clock className="size-3.5" aria-hidden /> {s.durationMinutes} min{s.description ? ` · ${s.description}` : ''}
                  </p>
                </Choice>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === 1 && service && (
        <ul className="grid gap-3 @2xl:grid-cols-2">
          <li className="@2xl:col-span-2">
            <Choice selected={barber === 'any'} onClick={() => pickBarber('any')}>
              <span className="text-lg font-semibold">Sem preferência</span>
              <p className="mt-1 text-sm" style={{ color: 'var(--s-muted)' }}>Mostramos todos os horários livres da equipe.</p>
            </Choice>
          </li>
          {barbersFor.map((b) => (
            <li key={b.id}>
              <Choice selected={barber !== 'any' && barber?.id === b.id} onClick={() => pickBarber(b)} className="flex items-center gap-4">
                {b.photoUrl ? (
                  <img src={b.photoUrl} alt="" width={56} height={56} loading="lazy" className="size-14 shrink-0 object-cover" />
                ) : (
                  <span aria-hidden className="flex size-14 shrink-0 items-center justify-center text-3xl" style={{ ...displayStyle, background: 'var(--s-line)' }}>{b.displayName.slice(0, 1)}</span>
                )}
                <span>
                  <span className="block text-lg font-semibold">{b.displayName}</span>
                  {b.specialties.length > 0 && <span className="block text-sm" style={{ color: 'var(--s-muted)' }}>{b.specialties.slice(0, 3).join(' · ')}</span>}
                </span>
              </Choice>
            </li>
          ))}
          {barbersFor.length === 0 && <li className="@2xl:col-span-2 text-sm" style={{ color: 'var(--s-muted)' }}>Nenhum profissional específico para este serviço nesta unidade.</li>}
        </ul>
      )}

      {step === 2 && (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-8">
          <div className="min-w-0">
            <p className="mb-3 text-sm font-semibold" id="bk-days">Escolha o dia</p>
            <div role="group" aria-labelledby="bk-days" className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-3 @2xl:mx-0 @2xl:px-0">
              {days.map((d) => {
                const dt = new Date(`${d}T12:00:00Z`);
                const sel = d === date;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => pickDate(d)}
                    aria-pressed={sel}
                    className="flex w-16 shrink-0 flex-col items-center border-2 py-2.5"
                    style={{ borderColor: sel ? 'var(--s-ink)' : 'var(--s-line)', background: sel ? 'var(--s-ink)' : undefined, color: sel ? 'var(--s-bg)' : undefined }}
                  >
                    <span className="text-xs font-semibold uppercase">{dt.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'UTC' }).replace('.', '')}</span>
                    <span className="tabular text-2xl font-bold" style={displayStyle}>{dt.getUTCDate()}</span>
                    <span className="text-xs">{dt.toLocaleDateString('pt-BR', { month: 'short', timeZone: 'UTC' }).replace('.', '')}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {date && (
            <div aria-live="polite">
              <p className="mb-3 text-sm font-semibold">Horários livres</p>
              {avail.isPending && <p style={{ color: 'var(--s-muted)' }}>Buscando horários…</p>}
              {avail.isError && (
                <p role="alert">
                  Não foi possível buscar os horários. <button className="font-semibold underline" onClick={() => avail.refetch()}>Tentar de novo</button>
                </p>
              )}
              {avail.isSuccess && slots.length === 0 && <p>Sem horários neste dia. Experimente outra data.</p>}
              {slots.length > 0 && (
                <ul className="grid grid-cols-3 gap-2 @2xl:grid-cols-5">
                  {slots.map((s) => (
                    <li key={s.startsAt}>
                      <button
                        type="button"
                        onClick={() => pickSlot(s)}
                        className="tabular w-full border-2 py-3 text-lg font-semibold hover:border-[var(--s-ink)]"
                        style={{ borderColor: slot?.startsAt === s.startsAt ? 'var(--s-ink)' : 'var(--s-line)' }}
                      >
                        {fmtTime(s.startsAt, tz)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {step === 3 && service && slot && (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 @4xl:grid-cols-[minmax(0,1fr)_20rem]">
          <form onSubmit={form.handleSubmit(submit)} noValidate className="grid gap-5">
            <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label>Não preencha<input tabIndex={-1} autoComplete="off" {...form.register('website')} /></label>
            </div>
            <div className="grid gap-1.5">
              <label htmlFor="bk-name" className="text-sm font-semibold">Nome</label>
              <input id="bk-name" autoComplete="name" className={field} style={line} aria-invalid={!!errors.name} {...form.register('name')} />
              {errors.name && <p role="alert" className="text-sm font-medium">Informe seu nome (mínimo 2 letras).</p>}
            </div>
            <div className="grid gap-1.5">
              <label htmlFor="bk-phone" className="text-sm font-semibold">Telefone com DDD</label>
              <MaskedInput id="bk-phone" mask="phone" autoComplete="tel" placeholder="(11) 99999-9999" className={cn(field, 'h-auto rounded-none border-2 shadow-none')} style={line} aria-invalid={!!errors.phone} {...form.register('phone')} />
              {errors.phone && <p role="alert" className="text-sm font-medium">Informe um telefone válido com DDD.</p>}
            </div>
            <div className="grid gap-1.5">
              <label htmlFor="bk-email" className="text-sm font-semibold">E-mail</label>
              <input id="bk-email" type="email" autoComplete="email" className={field} style={line} aria-invalid={!!errors.email} {...form.register('email')} />
              {errors.email && <p role="alert" className="text-sm font-medium">Informe um e-mail válido.</p>}
            </div>
            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" className="mt-1 size-5 shrink-0 accent-[var(--s-accent)]" {...form.register('marketingConsent')} />
              <span>Aceito receber novidades e promoções da barbearia por e-mail. Pode ficar desmarcado: isso não afeta o agendamento.</span>
            </label>
            <div>
              <SubmitButton busy={isSubmitting} />
            </div>
          </form>
          <aside aria-label="Resumo" className="h-fit border-2 p-5 text-sm" style={{ borderColor: 'var(--s-ink)' }}>
            <p className="mb-3 text-2xl" style={displayStyle}>Resumo</p>
            <dl className="grid gap-3">
              <Row k="Serviço" v={service.name} />
              <Row k="Profissional" v={slotBarber?.displayName ?? '—'} />
              <Row k="Quando" v={longDate(slot.startsAt, tz)} />
              <Row k="Duração" v={`${service.durationMinutes} min`} />
              <Row k="Valor" v={money(service.priceCents)} strong />
            </dl>
          </aside>
        </div>
      )}
    </Wrap>
  );
}

function SubmitButton({ busy }: { busy: boolean }) {
  return (
    <button type="submit" disabled={busy} className="inline-flex min-h-12 items-center px-8 text-base font-bold disabled:opacity-60" style={{ background: 'var(--s-accent)', color: 'var(--s-on-accent)' }}>
      {busy ? 'Agendando…' : 'Confirmar agendamento'}
    </button>
  );
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-semibold" style={{ color: 'var(--s-muted)' }}>{k}</dt>
      <dd className={cn('first-letter:uppercase', strong ? 'text-xl font-bold' : 'text-base')}>{v}</dd>
    </div>
  );
}

export function Wrap({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto min-h-[70svh] min-w-0 max-w-4xl px-5 py-12 @2xl:px-10 @4xl:py-16">{children}</div>;
}

